import { useMutation } from '@apollo/client'
import { Box, FormModal, Link, Text } from '@chaine/keychaine'
import React, { useState } from 'react'
import { SubmitHandler } from 'react-hook-form'

import { Toast } from '../../../_shared/components/toast'
import { validations } from '../../../_utils/validations/validations'
import { AppModalProps } from '../components/apps-data'
import { ENABLE_BIG_ROAD_APP, EnableBigRoadAppInput } from '../data/mutations'

/**
 * Saves BigRoad app's api key.
 * @param props.app the app
 * @param props.isOpen sets the modal to open or closed
 * @param props.onClose closes the modal
 */
const BigRoadModal = React.memo((props: AppModalProps) => {
  const { isOpen, onClose, appType, refetchApps } = props
  const { showToast } = Toast()
  const [formError, setFormError] = useState('')

  const [enableBigRoadApp, { loading }] = useMutation<{ enableBigRoadApp: { apiKey: string } }, EnableBigRoadAppInput>(
    ENABLE_BIG_ROAD_APP,
    {
      onCompleted: () => {
        showToast({
          status: 'success',
          title: `BigRoad app is enabled successfully!`
        })
        refetchApps()
        onClose()
        setFormError('')
      },
      onError: (err) => {
        if (err.message === 'Incorrect API key') {
          setFormError('Invalid api key. Please try again.')
          showToast({
            status: 'error',
            title: `Invalid api key. Please try again.`
          })
        } else {
          setFormError(`Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`)
          showToast({
            status: 'error',
            title: `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
          })
        }
      }
    }
  )

  const closeModal = () => {
    setFormError('')
    onClose()
  }

  type FieldValues = { apiKey: string; appUsername?: string }
  const handleSaveDetails: SubmitHandler<FieldValues> = (event) => {
    enableBigRoadApp({
      variables: { apiKey: event.apiKey, appType: appType }
    })
  }

  const footer = (
    <Box>
      <Text color="red.600">{formError}</Text>
      <Text>
        {`See `}
        <Link
          color="blue.600"
          href="https://handbook.chaineapp.com/product-guide/carriers/connect-eld/elds/bigroad"
          target="_blank"
          rel="noopener noreferrer"
        >
          detailed steps here.
        </Link>
      </Text>
    </Box>
  )

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={closeModal}
        title={`Enable ${appType} for Chaine`}
        subtitle={`Enabling this integration requires an admin BigRoad account. Enter your BigRoad api key.`}
        formSubmitHandler={handleSaveDetails}
        buttonText={'Enable'}
        initialValues={{}}
        validations={validations}
        isLoading={loading}
        footer={formError ? footer : <></>}
        modalItems={[
          {
            labelText: 'BigRoad Api Key',
            name: 'apiKey',
            placeHolder: 'Enter your BigRoad Api Key',
            type: 'input'
          }
        ]}
      />
    </>
  )
})

BigRoadModal.displayName = 'BigRoadModal'

export { BigRoadModal }
